import {
  WealthCalculationInputs,
  WealthCalculationResult,
  WealthYearlyBreakdownItem,
  FormulaTraceStep,
  CompoundingFrequency,
  ContributionFrequency,
} from '../types';

/**
  YamooPay Universal Wealth Mathematics Engine v1.0.0
  Deterministic, versioned, explainable financial math engine.
 */

export const WEALTH_MATH_VERSION = 'v1.0.0-P13-DETERMINISTIC';

// Helper to convert frequencies to periods per year
export function getPeriodsPerYear(freq: CompoundingFrequency | ContributionFrequency): number {
  switch (freq) {
    case 'daily':
      return 365;
    case 'weekly':
      return 52;
    case 'biweekly':
      return 26;
    case 'monthly':
      return 12;
    case 'quarterly':
      return 4;
    case 'semiannual':
      return 2;
    case 'annual':
    case 'one_time':
    default:
      return 1;
  }
}

/**
 * Calculates exact multiplicative real return rate:
 * Real Return = ((1 + Nominal) / (1 + Inflation)) - 1
 */
export function calculateExactRealReturnPct(nominalPct: number, inflationPct: number): number {
  const nominalDec = nominalPct / 100;
  const inflationDec = inflationPct / 100;
  if (inflationDec === -1) return 0;
  const realDec = (1 + nominalDec) / (1 + inflationDec) - 1;
  return realDec * 100;
}

/**
 * Universal Compound Interest & Future Value Engine
 */
export function calculateCompoundGrowth(inputs: WealthCalculationInputs): WealthCalculationResult {
  const {
    initialPrincipal,
    periodicContribution,
    nominalReturnRatePct,
    inflationRatePct,
    annualFeePct,
    timeHorizonYears,
    compoundingFrequency,
    contributionFrequency,
    contributionTiming,
    annualContributionIncreasePct = 0,
    currency = 'USD',
  } = inputs;

  const compoundingPeriodsPerYear = getPeriodsPerYear(compoundingFrequency);
  const contributionPeriodsPerYear = getPeriodsPerYear(contributionFrequency);
  const totalYears = Math.max(1, Math.min(100, timeHorizonYears));

  const nominalRateDecimal = nominalReturnRatePct / 100;
  const feeRateDecimal = annualFeePct / 100;
  const netReturnDecimal = Math.max(-0.99, nominalRateDecimal - feeRateDecimal);
  const inflationDecimal = inflationRatePct / 100;

  // Effective periodic rates
  const netPeriodicRate = netReturnDecimal / compoundingPeriodsPerYear;
  const grossPeriodicRate = nominalRateDecimal / compoundingPeriodsPerYear;

  const trace: FormulaTraceStep[] = [];
  trace.push({
    stepName: '1. Rate Initialization',
    formulaText: `Gross Rate = ${nominalReturnRatePct}%, Fee = ${annualFeePct}%, Net Annual Rate = ${(
      netReturnDecimal * 100
    ).toFixed(2)}%`,
    calculatedValue: `Net Annual Decimal: ${netReturnDecimal.toFixed(4)}`,
  });

  trace.push({
    stepName: '2. Multiplicative Real Return',
    formulaText: `((1 + Net Return) / (1 + Inflation)) - 1`,
    calculatedValue: `${calculateExactRealReturnPct(netReturnDecimal * 100, inflationRatePct).toFixed(2)}% real return`,
  });

  // Calculate year by year
  let currentGrossBalance = initialPrincipal;
  let currentNetBalance = initialPrincipal; // balance with fees
  let currentZeroFeeBalance = initialPrincipal; // balance with zero fees for fee impact comparison

  let cumulativeContributions = initialPrincipal;
  let currentYearlyContribution = periodicContribution * contributionPeriodsPerYear;

  const yearlyBreakdown: WealthYearlyBreakdownItem[] = [];

  for (let year = 1; year <= totalYears; year++) {
    const yearStartNetBalance = currentNetBalance;
    let yearContributionSum = 0;
    let yearFeeSum = 0;

    // Monthly or periodic steps within the year
    const periodsInYear = contributionPeriodsPerYear;
    const periodNetRate = netReturnDecimal / periodsInYear;
    const periodZeroFeeRate = nominalRateDecimal / periodsInYear;

    let periodContribution =
      periodicContribution * Math.pow(1 + annualContributionIncreasePct / 100, year - 1);

    for (let p = 1; p <= periodsInYear; p++) {
      yearContributionSum += periodContribution;

      // Timing check: BEGINNING_OF_PERIOD adds contribution before compounding
      if (contributionTiming === 'BEGINNING_OF_PERIOD') {
        currentNetBalance += periodContribution;
        currentZeroFeeBalance += periodContribution;
      }

      // Compound net balance
      const interestNet = currentNetBalance * periodNetRate;
      const feePeriod = currentNetBalance * (feeRateDecimal / periodsInYear);
      currentNetBalance += interestNet;
      yearFeeSum += feePeriod;

      // Compound zero fee balance
      currentZeroFeeBalance += currentZeroFeeBalance * periodZeroFeeRate;

      // Timing check: END_OF_PERIOD adds contribution after compounding
      if (contributionTiming === 'END_OF_PERIOD') {
        currentNetBalance += periodContribution;
        currentZeroFeeBalance += periodContribution;
      }
    }

    cumulativeContributions += yearContributionSum;

    // Real inflation-adjusted value in today's money
    const inflationFactor = Math.pow(1 + inflationDecimal, year);
    const endingBalanceReal = currentNetBalance / inflationFactor;
    const purchasingPowerRatio = 1 / inflationFactor;

    yearlyBreakdown.push({
      year,
      startingBalanceNominal: Math.round(yearStartNetBalance * 100) / 100,
      totalContributionsNominal: Math.round(cumulativeContributions * 100) / 100,
      yearContributionsNominal: Math.round(yearContributionSum * 100) / 100,
      interestEarnedNominal:
        Math.round((currentNetBalance - yearStartNetBalance - yearContributionSum) * 100) / 100,
      feesDeductedNominal: Math.round(yearFeeSum * 100) / 100,
      endingBalanceNominal: Math.round(currentNetBalance * 100) / 100,
      endingBalanceReal: Math.round(endingBalanceReal * 100) / 100,
      purchasingPowerRatio: Math.round(purchasingPowerRatio * 10000) / 10000,
    });
  }

  const nominalFutureValue = Math.round(currentNetBalance * 100) / 100;
  const zeroFeeFutureValue = Math.round(currentZeroFeeBalance * 100) / 100;
  const totalPrincipalInvested = Math.round(initialPrincipal * 100) / 100;
  const totalContributions = Math.round(cumulativeContributions * 100) / 100;
  const totalGrowth = Math.max(0, Math.round((nominalFutureValue - totalContributions) * 100) / 100);
  const lostGrowthDueToFees = Math.max(0, Math.round((zeroFeeFutureValue - nominalFutureValue) * 100) / 100);
  const totalFeesDeducted = Math.round(yearlyBreakdown.reduce((sum, y) => sum + y.feesDeductedNominal, 0) * 100) / 100;

  const realFutureValue =
    Math.round((nominalFutureValue / Math.pow(1 + inflationDecimal, totalYears)) * 100) / 100;

  const effectiveRealReturnPct = calculateExactRealReturnPct(
    nominalReturnRatePct - annualFeePct,
    inflationRatePct
  );

  const purchasingPowerErosionPct =
    Math.round((1 - 1 / Math.pow(1 + inflationDecimal, totalYears)) * 1000) / 10;

  trace.push({
    stepName: '3. Horizon Completion',
    formulaText: `Final Balance = Total Contributions ($${totalContributions.toLocaleString()}) + Total Net Growth ($${totalGrowth.toLocaleString()})`,
    calculatedValue: `$${nominalFutureValue.toLocaleString()} Nominal ($${realFutureValue.toLocaleString()} Real)`,
  });

  return {
    formulaKey: 'compound_annuity',
    formulaVersion: WEALTH_MATH_VERSION,
    nominalFutureValue,
    realFutureValue,
    totalPrincipalInvested,
    totalContributions,
    totalGrowth,
    totalFeesDeducted,
    lostGrowthDueToFees,
    effectiveRealReturnPct: Math.round(effectiveRealReturnPct * 100) / 100,
    purchasingPowerErosionPct,
    yearlyBreakdown,
    formulaTrace: trace,
    assumptionsSummary: {
      nominalReturnRate: `${nominalReturnRatePct}% annual gross`,
      inflationRate: `${inflationRatePct}% user assumption`,
      feeRate: `${annualFeePct}% annual fee`,
      timeHorizon: `${timeHorizonYears} years`,
      timing: contributionTiming === 'BEGINNING_OF_PERIOD' ? 'Beginning of Period (Annuity Due)' : 'End of Period (Ordinary Annuity)',
      sourceLabel: 'YamooPay Deterministic Universal Math Core',
    },
  };
}

/**
 * Goal Solver Engine: Solves monthly contribution required or years to reach goal
 */
export function calculateGoalSolver(inputs: WealthCalculationInputs): WealthCalculationResult {
  const target = inputs.targetGoalAmount || 100000;
  const baseResult = calculateCompoundGrowth(inputs);

  // Solves required monthly contribution for target
  const r = (inputs.nominalReturnRatePct - inputs.annualFeePct) / 100 / 12;
  const n = inputs.timeHorizonYears * 12;
  const PV = inputs.initialPrincipal;

  let monthlyRequired = 0;
  if (r === 0) {
    monthlyRequired = Math.max(0, (target - PV) / n);
  } else {
    const timingMult = inputs.contributionTiming === 'BEGINNING_OF_PERIOD' ? 1 + r : 1;
    const fvPV = PV * Math.pow(1 + r, n);
    const gapFV = target - fvPV;
    if (gapFV <= 0) {
      monthlyRequired = 0;
    } else {
      monthlyRequired = gapFV / (((Math.pow(1 + r, n) - 1) / r) * timingMult);
    }
  }

  // Solves time to goal (in years)
  let timeToGoalYears = inputs.timeHorizonYears;
  if (baseResult.nominalFutureValue < target && inputs.periodicContribution > 0) {
    let checkNet = inputs.initialPrincipal;
    let months = 0;
    const pmt = inputs.periodicContribution;
    while (checkNet < target && months < 1200) { // max 100 years
      months++;
      if (inputs.contributionTiming === 'BEGINNING_OF_PERIOD') checkNet += pmt;
      checkNet += checkNet * r;
      if (inputs.contributionTiming === 'END_OF_PERIOD') checkNet += pmt;
    }
    timeToGoalYears = Math.round((months / 12) * 10) / 10;
  } else if (baseResult.nominalFutureValue >= target) {
    timeToGoalYears = inputs.timeHorizonYears;
  }

  return {
    ...baseResult,
    formulaKey: 'goal_contribution_solver',
    monthlySavingsRequired: Math.round(monthlyRequired * 100) / 100,
    timeToGoalYears,
  };
}

/**
 * FIRE & Retirement Accumulation/Drawdown Engine
 */
export function calculateFIRERetirement(inputs: WealthCalculationInputs): WealthCalculationResult {
  const baseResult = calculateCompoundGrowth(inputs);
  const annualSpending = inputs.annualRetirementSpending || 40000;
  const withdrawalRate = inputs.withdrawalRatePct || 4.0; // default 4% heuristic

  // FI Target = Annual Spending / Withdrawal Rate (e.g., $40,000 / 0.04 = $1,000,000)
  const fireTargetAmount = Math.round((annualSpending / (withdrawalRate / 100)) * 100) / 100;

  // Estimate years to FIRE target based on current trajectory
  let fireYearsToTarget = 0;
  if (baseResult.nominalFutureValue < fireTargetAmount) {
    let checkNet = inputs.initialPrincipal;
    let months = 0;
    const pmt = inputs.periodicContribution;
    const r = (inputs.nominalReturnRatePct - inputs.annualFeePct) / 100 / 12;
    while (checkNet < fireTargetAmount && months < 1200) {
      months++;
      if (inputs.contributionTiming === 'BEGINNING_OF_PERIOD') checkNet += pmt;
      checkNet += checkNet * r;
      if (inputs.contributionTiming === 'END_OF_PERIOD') checkNet += pmt;
    }
    fireYearsToTarget = Math.round((months / 12) * 10) / 10;
  } else {
    fireYearsToTarget = inputs.timeHorizonYears;
  }

  // Calculate retirement decumulation sustainability starting with accumulated balance
  let decumulationBalance = baseResult.nominalFutureValue;
  let decumulationYears = 0;
  let yearlySpendingNominal = annualSpending * Math.pow(1 + inputs.inflationRatePct / 100, inputs.timeHorizonYears);
  const netReturnDec = (inputs.nominalReturnRatePct - inputs.annualFeePct) / 100;

  while (decumulationBalance > 0 && decumulationYears < 100) {
    decumulationYears++;
    decumulationBalance += decumulationBalance * netReturnDec;
    decumulationBalance -= yearlySpendingNominal;
    yearlySpendingNominal *= 1 + inputs.inflationRatePct / 100; // inflation-adjusted withdrawal
  }

  return {
    ...baseResult,
    formulaKey: 'fire_target',
    fireTargetAmount,
    fireYearsToTarget,
    retirementSustainabilityYears: decumulationBalance <= 0 ? decumulationYears : 100,
  };
}
